import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import Dialog from "../../dialog/Dialog";
import ImageInput from "../../common/ImageInput";
import { createRentalCategory } from "../../../services/rentalService";
import { RENTAL_FORM_TYPE_OPTIONS } from "../../../config/rentalCategoryFormConfig";

const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
];

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "38px",
        borderColor: "#d1d5db",
        borderRadius: "0.5rem",
        boxShadow: "none",
        "&:hover": { borderColor: "#d1d5db" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", fontSize: "14px" }),
    placeholder: (base) => ({ ...base, fontSize: "14px" }),
    singleValue: (base) => ({ ...base, fontSize: "14px" }),
    menu: (base) => ({ ...base, fontSize: "14px" }),
};

const EMPTY_FORM = {
    name: "",
    form_type: "",
    description: "",
    is_active: 1,
    image: null,
};

const RentalInlineCategoryCreateDialog = ({ open, onClose, onCreated }) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(EMPTY_FORM);
    const [nameError, setNameError] = useState("");
    const [formTypeError, setFormTypeError] = useState("");
    const [imageError, setImageError] = useState("");
    const [submitError, setSubmitError] = useState("");

    const reset = () => {
        setForm(EMPTY_FORM);
        setNameError("");
        setFormTypeError("");
        setImageError("");
        setSubmitError("");
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const mutation = useMutation({
        mutationFn: createRentalCategory,
        onSuccess: async (response) => {
            await queryClient.invalidateQueries({ queryKey: ["rentalCategories"] });
            const payload = response?.data?.data ?? response?.data ?? response;
            const newId = payload?.id;
            if (newId) {
                onCreated?.(newId);
            }
            handleClose();
        },
        onError: (error) => {
            setSubmitError(
                error?.response?.data?.message || "Failed to create category."
            );
        },
    });

    const buildFormData = () => {
        setNameError("");
        setFormTypeError("");
        setImageError("");
        setSubmitError("");

        const trimmedName = form.name.trim();
        if (!trimmedName) {
            setNameError("Category name is required.");
            return null;
        }
        if (!form.form_type) {
            setFormTypeError("Please select a category type.");
            return null;
        }
        if (!form.image) {
            setImageError("Category image is required.");
            return null;
        }

        const formData = new FormData();
        formData.append("name", trimmedName);
        formData.append("description", form.description || "");
        formData.append("form_type", form.form_type);
        formData.append("is_active", form.is_active === 1 ? "1" : "0");
        formData.append("image", form.image);
        return formData;
    };

    const handleSubmit = () => {
        const formData = buildFormData();
        if (!formData) return;
        mutation.mutate(formData);
    };

    const formTypeValue = form.form_type
        ? RENTAL_FORM_TYPE_OPTIONS.find((option) => option.value === form.form_type)
        : null;

    const statusValue = statusOptions.find((option) => option.value === form.is_active);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title="Add Category"
            width="w-[520px]"
            closeButtonText="Cancel"
            primaryButtonText="Save"
            primaryButtonAction={handleSubmit}
            isLoading={mutation.isPending}
            isPrimaryDisabled={!form.name.trim() || !form.form_type || !form.image}
        >
            <div className="space-y-4 p-1">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category name <span className="text-red-500">*</span>
                    </label>
                    <input
                        autoFocus
                        type="text"
                        value={form.name}
                        onChange={(e) => {
                            setForm((prev) => ({ ...prev, name: e.target.value }));
                            setNameError("");
                            setSubmitError("");
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit();
                        }}
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-primary ${
                            nameError ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="e.g. Vehicle"
                    />
                    {nameError ? (
                        <p className="text-xs font-medium text-red-600 mt-1">{nameError}</p>
                    ) : null}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category type <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={formTypeValue}
                        onChange={(option) => {
                            setForm((prev) => ({
                                ...prev,
                                form_type: option?.value || "",
                            }));
                            setFormTypeError("");
                            setSubmitError("");
                        }}
                        options={RENTAL_FORM_TYPE_OPTIONS}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Select category"
                        styles={selectStyles}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Choose which rental fields appear when listing items in this category.
                    </p>
                    {formTypeError ? (
                        <p className="text-xs font-medium text-red-600 mt-1">{formTypeError}</p>
                    ) : null}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        value={form.description}
                        onChange={(e) =>
                            setForm((prev) => ({ ...prev, description: e.target.value }))
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary resize-none"
                        rows={3}
                        placeholder="Briefly describe this category"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status <span className="text-red-500">*</span>
                    </label>
                    <Select
                        value={statusValue}
                        onChange={(option) =>
                            setForm((prev) => ({
                                ...prev,
                                is_active: option?.value ?? 1,
                            }))
                        }
                        options={statusOptions}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Select status"
                        styles={selectStyles}
                    />
                </div>

                <div>
                    <ImageInput
                        onChange={(image) => {
                            setForm((prev) => ({ ...prev, image }));
                            setImageError("");
                            setSubmitError("");
                        }}
                        error={imageError || null}
                        label="Category image"
                        required
                        className="w-full"
                        multiple={false}
                        showPreview
                    />
                    <p className="text-xs text-gray-500 mt-1">Recommended size: 300x300px</p>
                </div>

                {submitError ? (
                    <p className="text-xs font-medium text-red-600">{submitError}</p>
                ) : null}
            </div>
        </Dialog>
    );
};

export default RentalInlineCategoryCreateDialog;
